import type {StateStorage} from 'zustand/middleware'

/**
 * Wrap a string key/value backend (e.g. localStorage) with a time-to-live.
 *
 * Each write is stamped with the current time; a read past `ttlMs` since that
 * write returns null (and drops the stale entry), so the zustand `persist`
 * middleware falls back to the store's initial state. Used to expire persisted
 * filters — notably the selected date, whose natural default ("today") drifts
 * daily — so a stale session does not resurrect a filter set from days ago.
 *
 * `now` is injectable for testing.
 */
export function createTtlStorage(
  ttlMs: number,
  backend: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>,
  now: () => number = Date.now,
): StateStorage {
  return {
    getItem: name => {
      const raw = backend.getItem(name)
      if (!raw) return null
      try {
        const parsed = JSON.parse(raw) as {savedAt?: number; value?: string}
        if (
          typeof parsed.savedAt !== 'number' ||
          typeof parsed.value !== 'string' ||
          now() - parsed.savedAt > ttlMs
        ) {
          backend.removeItem(name)
          return null
        }
        return parsed.value
      } catch {
        // Corrupted / legacy payload — treat as absent.
        return null
      }
    },
    setItem: (name, value) => {
      backend.setItem(name, JSON.stringify({savedAt: now(), value}))
    },
    removeItem: name => backend.removeItem(name),
  }
}
